import React, { useState, useEffect, useCallback } from 'react';
import {
  Loader2,
  Plus,
  Edit2,
  Trash2,
  Eye,
  EyeOff,
  Save,
  X,
  Key,
  CheckCircle,
  XCircle,
  ExternalLink,
  RefreshCw,
  Shield,
  AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminVendorCredentials = () => {
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingCred, setEditingCred] = useState(null);
  const [formData, setFormData] = useState({
    vendor_code: '',
    vendor_name: '',
    login_url: '',
    username: '',
    password: '',
    api_key: '',
    notes: '',
    is_active: true
  });
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Testing connection
  const [testing, setTesting] = useState({});

  const fetchCredentials = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const res = await fetch(`${API_URL}/api/vendor-credentials?include_inactive=true`);
      if (!res.ok) throw new Error('Failed to fetch credentials');
      
      const data = await res.json();
      setCredentials(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCredentials();
  }, [fetchCredentials]);

  const handleOpenModal = (cred = null) => {
    if (cred) {
      setEditingCred(cred);
      setFormData({
        vendor_code: cred.vendor_code,
        vendor_name: cred.vendor_name,
        login_url: cred.login_url,
        username: cred.username,
        password: '', // Don't pre-fill password
        api_key: '',
        notes: cred.notes || '',
        is_active: cred.is_active
      });
    } else {
      setEditingCred(null);
      setFormData({
        vendor_code: '',
        vendor_name: '',
        login_url: '',
        username: '',
        password: '',
        api_key: '',
        notes: '',
        is_active: true
      });
    }
    setShowPassword(false);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCred(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const url = editingCred 
        ? `${API_URL}/api/vendor-credentials/${editingCred.vendor_code}`
        : `${API_URL}/api/vendor-credentials`;
      
      const method = editingCred ? 'PUT' : 'POST';
      
      // Build body - only include password if it was changed
      const body = {
        vendor_name: formData.vendor_name,
        login_url: formData.login_url,
        username: formData.username,
        notes: formData.notes,
        is_active: formData.is_active
      };
      
      if (!editingCred) {
        body.vendor_code = formData.vendor_code.toLowerCase();
      }
      
      if (formData.password) {
        body.password = formData.password;
      }
      
      if (formData.api_key) {
        body.api_key = formData.api_key;
      }
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save');
      }
      
      toast.success(editingCred ? 'Credentials updated' : 'Credentials created');
      handleCloseModal();
      fetchCredentials();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cred) => {
    if (!window.confirm(`Delete credentials for "${cred.vendor_name}"? This cannot be undone.`)) return;
    
    try {
      const res = await fetch(`${API_URL}/api/vendor-credentials/${cred.vendor_code}`, {
        method: 'DELETE'
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to delete');
      }
      
      toast.success('Credentials deleted');
      fetchCredentials();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleTestConnection = async (vendorCode) => {
    setTesting(prev => ({ ...prev, [vendorCode]: true }));
    
    try {
      const res = await fetch(`${API_URL}/api/vendor-credentials/${vendorCode}/test`, {
        method: 'POST'
      });
      
      const result = await res.json();
      
      if (result.success) {
        toast.success(`Connection to ${vendorCode} successful!`);
      } else {
        toast.error(`Connection failed: ${result.message}`);
      }
    } catch (err) {
      toast.error('Test failed: ' + err.message);
    } finally {
      setTesting(prev => ({ ...prev, [vendorCode]: false }));
    }
  };

  // Predefined vendor templates
  const vendorTemplates = [
    { code: '4over', name: '4over', url: 'https://www.4over.com/login' },
    { code: 'sanmar', name: 'SanMar', url: 'https://www.sanmar.com/login' },
    { code: 'alphabroder', name: 'AlphaBroder', url: 'https://www.alphabroder.com/login' },
  ];

  const applyTemplate = (template) => {
    setFormData(prev => ({
      ...prev,
      vendor_code: template.code,
      vendor_name: template.name,
      login_url: template.url
    }));
  };

  if (loading && credentials.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-gold-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vendor-credentials-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-heading font-semibold text-ivory-800 flex items-center gap-2">
            <Shield className="w-5 h-5 text-gold-600" />
            Vendor Credentials
          </h2>
          <p className="text-sm text-ivory-500 font-body">
            Securely store login credentials for vendor product syncing
          </p>
        </div>
        <Button onClick={() => handleOpenModal()} data-testid="add-credentials-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Credentials
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}

      {/* Credentials List */}
      {credentials.length === 0 ? (
        <Card className="bg-ivory-50">
          <CardContent className="py-12 text-center">
            <Key className="w-16 h-16 mx-auto text-ivory-300 mb-4" />
            <h3 className="text-lg font-medium text-ivory-700 mb-2">No Vendor Credentials</h3>
            <p className="text-ivory-500 mb-4">
              Add vendor login credentials to enable automatic product syncing
            </p>
            <Button onClick={() => handleOpenModal()}>
              <Plus className="w-4 h-4 mr-2" />
              Add First Credential
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {credentials.map((cred) => (
            <Card key={cred.id} className={`bg-ivory-50 border ${!cred.is_active ? 'opacity-60' : ''}`}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gold-500/10 flex items-center justify-center">
                      <Key className="w-6 h-6 text-gold-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-ivory-800">{cred.vendor_name}</h3>
                        <Badge variant={cred.is_active ? 'default' : 'secondary'} className="text-xs">
                          {cred.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <p className="text-sm text-ivory-500">
                        <span className="font-mono">{cred.vendor_code}</span> • {cred.username}
                      </p>
                      {cred.login_url && (
                        <a 
                          href={cred.login_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-xs text-gold-600 hover:underline flex items-center gap-1 mt-1"
                        >
                          {cred.login_url} <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    {/* Status indicators */}
                    <div className="text-right text-sm">
                      <div className="flex items-center gap-2 text-ivory-500">
                        {cred.password_set ? (
                          <span className="flex items-center gap-1 text-green-600">
                            <CheckCircle className="w-4 h-4" /> Password set
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-amber-600">
                            <XCircle className="w-4 h-4" /> No password
                          </span>
                        )}
                      </div>
                      {cred.last_used_at && (
                        <p className="text-xs text-ivory-400 mt-1">
                          Last used: {new Date(cred.last_used_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTestConnection(cred.vendor_code)}
                        disabled={testing[cred.vendor_code]}
                        title="Test connection"
                      >
                        {testing[cred.vendor_code] ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenModal(cred)}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(cred)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-ivory-200 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="font-semibold text-ivory-800 flex items-center gap-2">
                <Key className="w-5 h-5 text-gold-600" />
                {editingCred ? 'Edit Vendor Credentials' : 'Add Vendor Credentials'}
              </h3>
              <Button variant="ghost" size="sm" onClick={handleCloseModal}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Quick Templates (only for new) */}
              {!editingCred && (
                <div>
                  <label className="block text-sm font-medium text-ivory-700 mb-2">Quick Setup</label>
                  <div className="flex flex-wrap gap-2">
                    {vendorTemplates.map((template) => (
                      <Button
                        key={template.code}
                        variant="outline"
                        size="sm"
                        onClick={() => applyTemplate(template)}
                        className="text-xs"
                      >
                        {template.name}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Vendor Code */}
              <div>
                <label className="block text-sm font-medium text-ivory-700 mb-1">
                  Vendor Code *
                </label>
                <Input
                  value={formData.vendor_code}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    vendor_code: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') 
                  })}
                  placeholder="e.g., 4over, sanmar"
                  disabled={!!editingCred}
                  className={editingCred ? 'bg-ivory-50' : ''}
                />
                {editingCred && (
                  <p className="text-xs text-ivory-500 mt-1">Code cannot be changed after creation</p>
                )}
              </div>
              
              {/* Vendor Name */}
              <div>
                <label className="block text-sm font-medium text-ivory-700 mb-1">
                  Vendor Name *
                </label>
                <Input
                  value={formData.vendor_name}
                  onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })}
                  placeholder="e.g., 4over Wholesale Print"
                />
              </div>
              
              {/* Login URL */}
              <div>
                <label className="block text-sm font-medium text-ivory-700 mb-1">
                  Login URL *
                </label>
                <Input
                  value={formData.login_url}
                  onChange={(e) => setFormData({ ...formData, login_url: e.target.value })}
                  placeholder="https://www.4over.com/login"
                />
              </div>
              
              {/* Username */}
              <div>
                <label className="block text-sm font-medium text-ivory-700 mb-1">
                  Username / Email *
                </label>
                <Input
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="your@email.com"
                />
              </div>
              
              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-ivory-700 mb-1">
                  Password {editingCred ? '(leave blank to keep current)' : '*'}
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder={editingCred ? '••••••••' : 'Enter password'}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-ivory-400 hover:text-ivory-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              {/* API Key (optional) */}
              <div>
                <label className="block text-sm font-medium text-ivory-700 mb-1">
                  API Key (optional)
                </label>
                <Input
                  value={formData.api_key}
                  onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                  placeholder="For vendors with API access"
                />
              </div>
              
              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-ivory-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any additional notes..."
                  className="w-full px-3 py-2 border border-ivory-300 rounded-lg resize-none h-20"
                />
              </div>
              
              {/* Active toggle */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is-active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded"
                />
                <label htmlFor="is-active" className="text-sm text-ivory-700">
                  Active (enable for syncing)
                </label>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-ivory-200 flex justify-end gap-3 sticky bottom-0 bg-white">
              <Button variant="outline" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={saving || !formData.vendor_code || !formData.vendor_name || !formData.login_url || !formData.username || (!editingCred && !formData.password)}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                {editingCred ? 'Update' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVendorCredentials;
